import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const faqs = [
  {
    question: "How quickly can we get started?",
    answer:
      "You can get started in minutes. Connect your systems, let the AI analyze your network, and unlock actionable insights without complex setup.",
  },
  {
    question: "Do you offer a free trial?",
    answer:
      "Yes. A free trial is available with guided access to selected platform capabilities.",
  },
  {
    question: "What does pricing look like?",
    answer:
      "We follow a modular pricing model based on selected solutions, integrations, and transaction volume. Customized proposals are provided based on operational scope.",
  },
];

function FAQItem({ question, answer, isOpen, onToggle, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.45, delay: index * 0.1 }}
      className="border-b border-gray-200 last:border-b-0"
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-4 py-5 text-left group"
      >
        {/* +/- button */}
        <span
          className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 transition-colors duration-200 ${
            isOpen ? "bg-orange-500" : "bg-orange-500"
          }`}
        >
          <motion.svg
            viewBox="0 0 20 20"
            fill="none"
            className="w-4 h-4 text-white"
            xmlns="http://www.w3.org/2000/svg"
            animate={{ rotate: isOpen ? 0 : 0 }}
          >
            {isOpen ? (
              <rect x="4" y="9" width="12" height="2.2" rx="1.1" fill="white" />
            ) : (
              <>
                <rect x="4" y="9" width="12" height="2.2" rx="1.1" fill="white" />
                <rect x="9" y="4" width="2.2" height="12" rx="1.1" fill="white" />
              </>
            )}
          </motion.svg>
        </span>

        <span
          className={`font-bold text-[1.05rem] font-jakarta transition-colors duration-200 ${
            isOpen ? "text-orange-500" : "text-gray-900"
          }`}
        >
          {question}
        </span>
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            key="answer"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.32, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <p className="pb-5 pl-12 text-gray-500 text-[0.95rem] leading-relaxed">
              {answer}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <section className="w-full bg-white py-8 lg:py-12 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 flex flex-col lg:flex-row items-center gap-12">

        {/* ── LEFT: FAQ ── */}
        <div className="flex-1 max-w-xl w-full">
          {/* Badge */}
           {/* <motion.div
            initial={{ opacity: 0, y: -12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45 }}
            className="mb-5"
          >
            <span className="inline-block px-5 py-1.5 font-jakarta rounded-full border border-gray-300 bg-gray-50 text-gray-600 text-xs font-semibold tracking-widest uppercase">
              your answer
            </span>
          </motion.div> */}

          {/* Heading */}
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55, delay: 0.1 }}
            className="text-2xl md:text-4xl lg:text-[45px] font-extrabold text-gray-900 leading-[2.25] mb-8"
          >
            Have Any <span className="text-orange-500">Question</span> Please?
          </motion.h2>

          {/* FAQ Items */}
          <div>
            {faqs.map((faq, i) => (
              <FAQItem
                key={faq.question}
                question={faq.question}
                answer={faq.answer}
                isOpen={openIndex === i}
                onToggle={() => setOpenIndex(openIndex === i ? null : i)}
                index={i}
              />
            ))}
          </div>
        </div>

        {/* ── RIGHT: Video / Image ── */}
        <motion.div
          className="flex-1 w-full relative"
          initial={{ opacity: 0, x: 50 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.65, ease: "easeOut" }}
        >
          <div className="relative rounded-2xl overflow-hidden" style={{ minHeight: "420px" }}>
            <img
              src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=900&auto=format&fit=crop&q=80"
              alt="Team presentation meeting"
              className="w-full h-full object-cover"
              style={{ minHeight: "420px", objectPosition: "center" }}
            />

            {/* Play button */}
            {/* <div className="absolute inset-0 flex items-start justify-center pt-8">
              <motion.button
                whileHover={{ scale: 1.12 }}
                whileTap={{ scale: 0.95 }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                className="w-14 h-14 bg-white rounded-full flex items-center justify-center shadow-lg"
              >
                <motion.span
                  className="absolute w-14 h-14 rounded-full bg-white opacity-30"
                  animate={{ scale: [1, 1.5, 1], opacity: [0.3, 0, 0.3] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                />
                <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6 text-orange-500 ml-1" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5 3l14 9-14 9V3z" fill="currentColor" />
                </svg>
              </motion.button>
            </div> */}
          </div>
        </motion.div>
      </div>
    </section>
  );
}